import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Clock, KeyRound, Link2 } from "lucide-react";
import { utenzeApi } from "@/api/utenze";
import { useAuth } from "@/auth/useAuth";
import { RiaccediDialog } from "./RiaccediDialog";
import type { StatoUtenza, UtenzaFornitoreInfo } from "@/types";

const OGNI_MINUTO = 60_000;

// Colore semantico + icona: lo stato non si legge dal solo colore (regola a11y
// "color-not-only"). Neutro per il caso normale, ambra per ciò che l'affiliato
// può rimettere a posto da solo, rosso per ciò che richiede di rifare le credenziali.
const TONO: Record<StatoUtenza, string> = {
  attiva: "bg-muted text-muted-foreground border-transparent",
  sessione_scaduta: "bg-amber-50 text-amber-700 border-amber-200",
  da_ripristinare: "bg-red-50 text-red-700 border-red-200",
  // le credenziali mai inserite non sono un guasto: tono neutro come l'utenza attiva
  da_configurare: "bg-muted text-muted-foreground border-transparent",
};

const ICONA: Record<StatoUtenza, typeof Link2> = {
  attiva: Link2,
  sessione_scaduta: Clock,
  da_ripristinare: AlertTriangle,
  da_configurare: KeyRound,
};

function oraAccesso(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
}

function etichetta(u: UtenzaFornitoreInfo): string {
  if (u.stato === "sessione_scaduta") return `${u.fornitore} · sessione scaduta`;
  if (u.stato === "da_ripristinare") return `${u.fornitore} · accesso da ripristinare`;
  if (u.stato === "da_configurare") return `${u.fornitore} · credenziali da inserire`;
  const ora = oraAccesso(u.ultimo_accesso_ok_at);
  return ora ? `${u.fornitore} · collegata · ultimo accesso ${ora}` : `${u.fornitore} · collegata`;
}

/**
 * Riga di stato delle utenze fornitore dell'agenzia, sotto la testata.
 * Mostra solo le utenze proprie (la predefinita del portale non riguarda l'agenzia)
 * e sparisce del tutto quando non c'è nulla da dire.
 */
export function StatoUtenzeStrip() {
  const { hasRole } = useAuth();
  const [utenze, setUtenze] = useState<UtenzaFornitoreInfo[]>([]);
  const [daRiaccedere, setDaRiaccedere] = useState<UtenzaFornitoreInfo | null>(null);

  const carica = useCallback(async () => {
    try {
      setUtenze(await utenzeApi.lista());
    } catch {
      // La riga di stato è accessoria: se la chiamata non riesce non si mostra nulla
      // e non si disturba l'utente con un errore in testata.
    }
  }, []);

  useEffect(() => {
    carica();
    const t = setInterval(carica, OGNI_MINUTO);
    return () => clearInterval(t);
  }, [carica]);

  const mie = utenze.filter((u) => u.mia && u.stato !== null);
  if (mie.length === 0) return null;

  const affiliato = hasRole("affiliato");

  return (
    <div data-tour="stato-utenze" className="px-4 md:px-6 pt-3 flex flex-wrap items-center gap-2">
      {mie.map((u) => {
        const stato = u.stato as StatoUtenza;
        const Icona = ICONA[stato];
        return (
          <span
            key={u.fornitore}
            className={`inline-flex max-w-full flex-wrap items-center gap-x-1.5 gap-y-0.5 rounded-lg border px-2.5 py-1 text-xs ${TONO[stato]}`}
          >
            <Icona size={13} aria-hidden="true" className="shrink-0" />
            <span>{etichetta(u)}</span>
            {u.motivo_stato && <span className="opacity-80">· {u.motivo_stato}</span>}
            {stato === "sessione_scaduta" &&
              (affiliato ? (
                <button
                  type="button"
                  onClick={() => setDaRiaccedere(u)}
                  className="ml-0.5 font-medium underline underline-offset-2 cursor-pointer rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                >
                  Riaccedi
                </button>
              ) : (
                <span className="ml-0.5">· chiedi all'Affiliato di riaccedere</span>
              ))}
            {stato === "da_ripristinare" && (
              <Link
                to="/agenzia/profilo"
                className="ml-0.5 font-medium underline underline-offset-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
              >
                Profilo
              </Link>
            )}
          </span>
        );
      })}

      {daRiaccedere && (
        <RiaccediDialog
          fornitore={daRiaccedere.fornitore}
          username={daRiaccedere.username ?? ""}
          open
          onClose={() => setDaRiaccedere(null)}
          onSuccess={() => {
            setDaRiaccedere(null);
            carica();
          }}
        />
      )}
    </div>
  );
}
